import { useMemoize } from "@vueuse/core";
import { setBuffer } from "../interop/bufferManager";

const FONT_FACE_MAP = new Map<string, FontFace>();
const toFontKeyFromParts = (family: string, weight: string, style: string) => `${family}|${weight}|${style}`;
const toFontKey = (font: FontFace) => toFontKeyFromParts(font.family, font.weight, font.style);

// because Firefox throws 'FontFaceSet.entries() is not iterable' error
// helper function to convert 'document.fonts' to flat array
function fontFaceSetIteratorToArray(target: FontFaceSet): FontFace[] {
  const iterable = target.entries();
  const results = [];
  let iterator = iterable.next();

  while (iterator.done === false) {
    results.push(iterator.value);

    iterator = iterable.next();
  }

  return results.flat();
}

const fonts = fontFaceSetIteratorToArray(document.fonts);
for (const font of fonts) {
  FONT_FACE_MAP.set(toFontKey(font), font);
}

function loadFontToDocument(family: string, weight: string, isItalic: boolean, buffer: ArrayBuffer) {
  const font = new FontFace(family, buffer, {
    style: isItalic ? "italic" : "normal",
    weight,
  });

  document.fonts.add(font);
  FONT_FACE_MAP.set(toFontKey(font), font);
}

function isFontAlreadyExists(family: string, weight: string, isItalic: boolean) {
  const fontKey = toFontKeyFromParts(family, weight, (isItalic ? "italic" : "normal"));
  return FONT_FACE_MAP.has(fontKey);
}

const getFont = useMemoize(async (url: string): Promise<ArrayBuffer> => fetch(url, { mode: "cors" })
  .then(response => response.arrayBuffer())
  .catch(() => {
    throw new Error(`Failed to fetch font from ${url}`);
  }));

export async function loadFont(family: string, weight: string, isItalic: boolean, url: string): Promise<number> {
  const buffer = await getFont(url);

  if (!isFontAlreadyExists(family, weight, isItalic)) {
    loadFontToDocument(family, weight, isItalic, buffer);
  }

  const bufferId = setBuffer(buffer);
  return bufferId;
}

export async function loadBundledFont(family: string, weight: string, isItalic: boolean, bufferPtr: number, dataLength: number) {
  const bufferArray = new Uint8Array(Module.HEAPU8.buffer, bufferPtr, dataLength);
  const arrayBuffer = bufferArray.buffer.slice(bufferArray.byteOffset, bufferArray.byteOffset + bufferArray.byteLength) as ArrayBuffer;
  if (!isFontAlreadyExists(family, weight, isItalic)) {
    loadFontToDocument(family, weight, isItalic, arrayBuffer);
  }
}
